import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2, Upload, Download, Building2, Tag, Loader2, Database } from "lucide-react";
import api, { API, apiErr } from "@/lib/api";
import { fmtEUR } from "@/lib/format";
import { CARD, Label, Loader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label as UiLabel } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

export default function Parametres() {
  const [settings, setSettings] = useState(null);
  const [sites, setSites] = useState([]);
  const [cats, setCats] = useState([]);
  const [rev, setRev] = useState(0);
  const [saving, setSaving] = useState(false);
  const [newSite, setNewSite] = useState("");
  const [newCat, setNewCat] = useState({ name: "", type: "expense" });
  const fileRef = useRef();

  const load = useCallback(async () => {
    try {
      const [s, si, c] = await Promise.all([api.get("/settings"), api.get("/sites"), api.get("/categories")]);
      setSettings(s.data); setSites(si.data); setCats(c.data);
    } finally { setRev((r) => r + 1); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        starting_balance: parseFloat(settings.starting_balance) || 0,
        safety_threshold: parseFloat(settings.safety_threshold) || 0,
        social_charge_rate: parseFloat(settings.social_charge_rate) || 0,
        anchor_year: parseInt(settings.anchor_year), anchor_month: parseInt(settings.anchor_month),
      });
      toast.success("Paramètres enregistrés");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const addSite = async () => { if (!newSite) return; await api.post("/sites", { name: newSite }); setNewSite(""); toast.success("Site ajouté"); load(); };
  const delSite = async (id) => { await api.delete(`/sites/${id}`); load(); };
  const addCat = async () => { if (!newCat.name) return; await api.post("/categories", newCat); setNewCat({ name: "", type: "expense" }); toast.success("Catégorie ajoutée"); load(); };
  const delCat = async (id) => { await api.delete(`/categories/${id}`); load(); };
  const saveAcc = async (c, v) => {
    if ((c.account_code || "") === v.trim()) return;
    try { await api.put(`/categories/${c.id}`, { account_code: v }); toast.success(`Compte enregistré pour ${c.name}`); load(); } catch (e) { toast.error(apiErr(e)); load(); }
  };

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.imported} mouvements importés`);
    } catch (err) { toast.error(apiErr(err)); }
    e.target.value = "";
  };

  const seedDemo = async () => {
    try { await api.post("/onboarding/seed-demo"); toast.success("Données de démonstration chargées"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!settings) return <Loader />;
  const set = (k, v) => setSettings({ ...settings, [k]: v });

  return (
    <div>
      <div>
        <p className="text-sm text-gray-400">Configuration</p>
        <h1 className="font-display text-2xl font-bold sm:text-3xl">Paramètres</h1>
      </div>

      <Tabs defaultValue="general" className="mt-5">
        <TabsList className="rounded-full bg-gray-100 p-1">
          <TabsTrigger value="general" className="rounded-full">Général</TabsTrigger>
          <TabsTrigger value="sites" className="rounded-full">Sites</TabsTrigger>
          <TabsTrigger value="cats" className="rounded-full">Catégories</TabsTrigger>
          <TabsTrigger value="data" className="rounded-full">Données</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-5">
          <div className={`${CARD} space-y-5 p-6`}>
            <div>
              <UiLabel>Mois de départ de la trésorerie</UiLabel>
              <div className="mt-1.5 flex gap-2">
                <Select value={String(parseInt(settings.anchor_month))} onValueChange={(v) => set("anchor_month", v)}>
                  <SelectTrigger data-testid="setting-anchor-month" className="flex-1 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTHS.map((n, i) => <SelectItem key={n} value={String(i + 1)}>{n}</SelectItem>)}</SelectContent>
                </Select>
                <Input data-testid="setting-anchor-year" type="number" min="2000" max="2100" value={settings.anchor_year} onChange={(e) => set("anchor_year", e.target.value)} className="w-28 rounded-xl" />
              </div>
              <p className="mt-1 text-xs text-gray-500">Les mouvements des mois précédents ne sont pas comptés dans la trésorerie (ils restent visibles dans Mouvements et Analyses). Les salaires et charges sont générés automatiquement à partir de ce mois.</p>
            </div>
            <div>
              <UiLabel>Solde en banque au 1er jour du mois de départ (€)</UiLabel>
              <Input data-testid="setting-balance" type="number" value={settings.starting_balance ?? 0} onChange={(e) => set("starting_balance", e.target.value)} className="mt-1.5 rounded-xl" />
              <p className="mt-1 text-xs text-gray-400">Base de toutes les projections de trésorerie.</p>
            </div>
            <div>
              <UiLabel>Seuil minimum de sécurité (€)</UiLabel>
              <Input data-testid="setting-threshold" type="number" value={settings.safety_threshold ?? 0} onChange={(e) => set("safety_threshold", e.target.value)} className="mt-1.5 rounded-xl" />
              <p className="mt-1 text-xs text-gray-400">Une alerte apparaît si la trésorerie passe sous ce seuil.</p>
            </div>
            <div>
              <UiLabel>Taux de charges sociales par défaut (%)</UiLabel>
              <Input data-testid="setting-rate" type="number" step="0.01" value={((settings.social_charge_rate ?? 0.3158) * 100).toFixed(2)} onChange={(e) => set("social_charge_rate", (parseFloat(e.target.value) || 0) / 100)} className="mt-1.5 rounded-xl" />
            </div>
            <Button data-testid="save-settings" onClick={saveSettings} disabled={saving} className="rounded-full bg-[#065F46] hover:bg-[#047857]">
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}Enregistrer
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="sites" className="mt-5">
          <div className={`${CARD} p-6`}>
            <div className="flex items-center gap-2 text-gray-400"><Building2 className="h-4 w-4" /><Label>Établissements</Label></div>
            <div className="mt-4 flex gap-2">
              <Input value={newSite} onChange={(e) => setNewSite(e.target.value)} placeholder="Nouveau site" className="rounded-xl" data-testid="new-site-input" />
              <Button onClick={addSite} className="rounded-full bg-[#065F46]" data-testid="add-site-btn"><Plus className="h-4 w-4" /></Button>
            </div>
            <div className="mt-4 divide-y divide-gray-50">
              {sites.map((s) => (
                <div key={s.id} className="flex items-center justify-between py-3">
                  <span className="text-sm font-semibold">{s.name}</span>
                  <button onClick={() => delSite(s.id)} className="rounded-lg p-1.5 text-gray-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="cats" className="mt-5">
          <div className={`${CARD} p-6`}>
            <div className="flex items-center gap-2 text-gray-400"><Tag className="h-4 w-4" /><Label>Catégories</Label></div>
            <div className="mt-4 flex gap-2">
              <Input value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} placeholder="Nouvelle catégorie" className="rounded-xl" data-testid="new-cat-input" />
              <select value={newCat.type} onChange={(e) => setNewCat({ ...newCat, type: e.target.value })} className="rounded-xl border border-gray-200 px-3 text-sm">
                <option value="expense">Dépense</option>
                <option value="income">Recette</option>
              </select>
              <Button onClick={addCat} className="rounded-full bg-[#065F46]" data-testid="add-cat-btn"><Plus className="h-4 w-4" /></Button>
            </div>
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              <p className="text-xs text-gray-500 sm:col-span-2">Indiquez le compte comptable de chaque catégorie de dépense : il est repris automatiquement sur les factures de cette catégorie et dans le FEC.</p>
              {cats.map((c) => (
                <div key={c.id} className="flex min-w-0 flex-wrap items-center justify-between gap-2 rounded-2xl bg-gray-50 px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: c.color }} />
                    <span className="text-sm font-medium">{c.name}</span>
                    <span className={`text-[10px] font-semibold ${c.type === "income" ? "text-emerald-600" : "text-rose-500"}`}>{c.type === "income" ? "recette" : "dépense"}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {c.type === "expense" && <Input key={`${c.id}-${rev}`} data-testid={`cat-account-${c.id}`} defaultValue={c.account_code || ""} placeholder="Compte (ex. 606000)" inputMode="numeric"
                      onBlur={(e) => saveAcc(c, e.target.value)} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} className="h-8 w-32 rounded-lg bg-white text-xs sm:w-36" title="Compte comptable utilisé dans le FEC des factures" />}
                    <button onClick={() => delCat(c.id)} className="text-gray-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="data" className="mt-5 space-y-5">
          <div className={`${CARD} p-6`}>
            <Label className="mb-3">Import / Export</Label>
            <p className="text-sm text-gray-500">Importez votre ancien fichier de suivi (.xlsx ou .csv). Colonnes reconnues : Date, Type, Catégorie, Libellé, Montant, Statut.</p>
            <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={doImport} data-testid="import-input" />
            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={() => fileRef.current?.click()} variant="outline" className="rounded-full" data-testid="import-btn"><Upload className="mr-1.5 h-4 w-4" />Importer</Button>
              <a href={`${API}/export?format=xlsx`}><Button variant="outline" className="rounded-full"><Download className="mr-1.5 h-4 w-4" />Export Excel</Button></a>
              <a href={`${API}/export?format=csv`}><Button variant="outline" className="rounded-full"><Download className="mr-1.5 h-4 w-4" />Export CSV</Button></a>
            </div>
          </div>
          <div className={`${CARD} p-6`}>
            <div className="flex items-center gap-2 text-gray-400"><Database className="h-4 w-4" /><Label>Données de démonstration</Label></div>
            <p className="mt-2 text-sm text-gray-500">Recharge les catégories, sites, salariés et mouvements de démonstration (si la base est vide).</p>
            <Button onClick={seedDemo} variant="outline" className="mt-4 rounded-full" data-testid="seed-btn">Charger la démo</Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
